import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { MANAGEMENT_URL } from "@/config/env";
import type { ManagedRepo, RepoFile, RepoTreeEntry } from "@/types";

// #270 (UC-1): Git-Content-Manager — Repos/Tree/File über den Master (der die Gitea-Cred
// hält; die UI kennt nie ein Gitea-Credential). Tree/File sind pfad-parametrisierte Queries.

/**
 * Die managebaren Repos.
 *
 * `scope: "mine"` (#283/#321 M-11) liefert dieselbe Liste über die `:self`-Tür — die
 * Workspaces der eigenen Agents UND die Ablage der eigenen Prozesse (`prc-<id>`). Wer nur
 * `git-content:read:self` hat (ein Prozess-Verantwortlicher ohne Admin-Rechte), bekommt
 * auf die Admin-Whitelist ein 403; deshalb ist die Wahl des Scopes eine ANZEIGE-Frage des
 * Aufrufers, keine Server-Frage. Ohne Scope bleibt es die Admin-Whitelist.
 *
 * Der Scope gehört in den Query-Key: die beiden Listen sind verschieden lang, und ein
 * gemeinsamer Cache-Eintrag zeigte dem einen die Liste des anderen. `enabled` erlaubt dem
 * Aufrufer, gar nicht erst zu fragen, solange er den Scope noch nicht kennt.
 */
export function useManagedRepos(
  scope?: "mine",
  opts: { enabled?: boolean } = {},
): UseQueryResult<ManagedRepo[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: ["resources", "git-content", "repos", scope ?? "_all"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<ManagedRepo[]>(`/git-content/repos${scope ? `?scope=${scope}` : ""}`);
    },
    enabled: !!session && (opts.enabled ?? true),
  });
}

// #278: der Ref geht in JEDEN QueryKey. Ohne das zeigte der TanStack-Cache Live-Inhalte im
// Entwurf (und umgekehrt) — in einer Freigabe-Oberfläche die schlimmste mögliche Fehlanzeige.
export function useRepoTree(repoId: string | undefined, path: string, ref?: string): UseQueryResult<RepoTreeEntry[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: ["resources", "git-content", "tree", repoId ?? "_none", path, ref ?? "_live"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const q = ref ? `&ref=${encodeURIComponent(ref)}` : "";
      return client.get<RepoTreeEntry[]>(`/git-content/repos/${repoId}/tree?path=${encodeURIComponent(path)}${q}`);
    },
    enabled: !!session && !!repoId,
  });
}

export function useRepoFile(repoId: string | undefined, path: string | null, ref?: string): UseQueryResult<RepoFile> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: ["resources", "git-content", "file", repoId ?? "_none", path ?? "_none", ref ?? "_live"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      const q = ref ? `&ref=${encodeURIComponent(ref)}` : "";
      return client.get<RepoFile>(`/git-content/repos/${repoId}/file?path=${encodeURIComponent(path ?? "")}${q}`);
    },
    enabled: !!session && !!repoId && !!path,
  });
}

// Raw-Download: Bytes mit Bearer-Header holen und als Datei speichern (kein <a href>,
// weil der Endpoint Auth braucht). Fehler wirft — Aufrufer toastet.
export async function downloadRepoFile(token: string, repoId: string, path: string, name: string, ref?: string): Promise<void> {
  const res = await fetch(
    `${MANAGEMENT_URL}/git-content/repos/${repoId}/download?path=${encodeURIComponent(path)}${ref ? `&ref=${encodeURIComponent(ref)}` : ""}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error(`Download fehlgeschlagen (HTTP ${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// #290 (UC-6): Diagnose — Repos ohne Artefakt-Bezug (Leichen). R1: Agent-Workspaces
// sind serverseitig strukturell ausgeschlossen.
// #291 (UC-5): getrennt in Waisen (archivierbar) und unsere Archive (read-only) —
// Entwurfs-referenzierte Repos tauchen serverseitig gar nicht erst auf.
export interface UnassignedRepos {
  /** false = Aussage nicht beurteilbar (Störung) → Listen bewusst leer (default-closed);
   *  die Sidebar benennt das, statt „nichts zu tun" zu suggerieren. */
  complete: boolean;
  orphans: Array<{ name: string; fullName: string }>;
  archives: Array<{ name: string; fullName: string }>;
}

export function useUnassignedRepos(): UseQueryResult<UnassignedRepos> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: ["resources", "git-content", "unassigned"],
    queryFn: () => {
      const client = createApiClient({ getToken: () => session?.accessToken ?? null, onUnauthorized: logout });
      return client.get<UnassignedRepos>("/git-content/unassigned");
    },
    enabled: !!session,
  });
}
